import { useId, useState } from 'react'
import { ArrowUpRight } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { kExternalStorageProviderDisplayName } from '@/constants/constants-external-storage'
import type { ConnectedAccount } from '@/settings/schemas/external-storage-schema'
import { SelectExternalFolderModal } from '@/user_files/external-storage/components/SelectExternalFolderModal'
import type { ExternalFolderSelection } from '@/user_files/external-storage/types'

interface ExternalFolderFieldProps {
  account: ConnectedAccount
  value: ExternalFolderSelection | null
  onChange: (selection: ExternalFolderSelection | null) => void
  disabled?: boolean
}

/** Stage a folder choice in the existing picker; saving the new matter starts synchronization. */
export function ExternalFolderField({ account, value, onChange, disabled = false }: ExternalFolderFieldProps) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const providerLabel = kExternalStorageProviderDisplayName[account.provider]

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex min-h-8 items-center justify-between gap-2">
        <label htmlFor={id} className="text-base font-medium leading-6 tracking-[-0.32px] text-text-primary">
          Sync a {providerLabel} folder
        </label>
        {value && (
          <Button type="button" variant="plain" size="sm" disabled={disabled} onClick={() => onChange(null)} aria-label="Clear folder selection">
            Clear
          </Button>
        )}
      </div>
      <div className="flex min-w-0 items-center gap-2">
        <button
          id={id}
          type="button"
          disabled={disabled}
          className="group relative min-h-[56px] min-w-0 flex-1 rounded border border-border-field bg-surface-field py-4 pl-3 pr-10 text-left text-base font-normal tracking-[-0.32px] text-text-primary focus:border-border-field-active focus:outline-hidden focus:ring-0 disabled:cursor-not-allowed disabled:border-border-strong disabled:bg-surface-subtle disabled:text-brand-neutral-700"
          aria-describedby={`${id}-help`}
          aria-haspopup="dialog"
          aria-expanded={open && !disabled}
          title={value?.path}
          onClick={() => setOpen(true)}
        >
          <span className={`block truncate group-disabled:text-brand-neutral-700 ${value ? '' : 'text-text-tertiary'}`}>
            {value?.path ?? 'Choose a folder'}
          </span>
          <ArrowUpRight className="absolute inset-y-0 right-3 my-auto h-6 w-6 text-text-tertiary group-disabled:text-brand-neutral-400" aria-hidden />
        </button>
      </div>
      <div id={`${id}-help`}>
        <InlineMessage variant="neutral" className="pr-2">
          Files in this folder and its subfolders will sync to this matter after you save.
        </InlineMessage>
      </div>
      <SelectExternalFolderModal
        account={account}
        currentExternalFolderId={value?.folderId}
        open={open && !disabled}
        onOpenChange={setOpen}
        onSelect={onChange}
      />
    </div>
  )
}
